import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { FormCommunication } from '@/features/communication/types';
import { OptionProps } from '@/components/Select';
import Event from '@/model/Event';
import { States } from '@/helpers/common/states';
import api from '@/services/api';
import CommunicationType from '@/model/CommunicationType';
import Communication from '@/model/Communication';
import CreateCommunication from '@/model/CreateCommunication';
import { communicationService } from '../../services';
import { CommunicationEditUI } from './ui';
import {
  getCommunicationPeriodError,
  toCommunicationDateISOString,
  toDateTimeLocal,
} from '../../utils/date';

interface IbgeMunicipioDTO {
  id: number;
  nome: string;
}

export const CommunicationEditScreen: React.FC = (): JSX.Element => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [events, setEvents] = useState<OptionProps[]>([]);
  const [state, setStatus] = useState<States>(States.default);
  const [type, setType] = useState<'TEMPLATE' | 'CUSTOM'>('TEMPLATE');
  const [cities, setCities] = useState<OptionProps[]>([]);
  const [selectedCities, setSelectedCities] = useState<OptionProps[]>([]);
  const [currentCityOption, setCurrentCityOption] = useState<OptionProps | null>(null);
  const [imageFileName, setImageFileName] = useState('');

  const {
    formData: formDataCommunication,
    formErrors: formErrorsCommunication,
    onChangeFormInput: onChangeFormInputCommunication,
  } = useForm({
    initialData: {
      name: '',
      chanel: '',
      city: '',
      country: '',
      radius: '',
      type_communication: 'TEMPLATE',
      template: '',
      imageBase64: '',
      messages: '',
      events: '',
      messageAmount: '',
      startDate: '',
      endDate: '',
    },
    validators: {
      name: [validators.required],
      country: [validators.required],
      city: [validators.required],
      radius: [validators.required],
      events: [validators.required],
      messageAmount: [validators.required],
      startDate: [validators.required],
      endDate: [validators.required],
    },
  });

  const handleOnGetEvents = async (): Promise<void> => {
    try {
      const { data } = await api.get<Event[]>('/order/event');

      if (data?.length > 0) {
        const eventsOptions: OptionProps[] = data.map(item => ({
          value: item.id,
          label: item.name,
        }));

        setEvents(eventsOptions);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    }
  };

  const handleCommunicationType = (chanel: string): CommunicationType => {
    switch (chanel) {
      case 'whatsapp':
        return CommunicationType.WHATSAPP;
      case 'gmail':
        return CommunicationType.EMAIL;
      case 'notification':
        return CommunicationType.PUSH_NOTIFICATION;
      default:
        return CommunicationType.WHATSAPP;
    }
  };

  const handleCommunicationTypeToString = (chanel: CommunicationType): string => {
    switch (chanel) {
      case CommunicationType.WHATSAPP:
        return 'whatsapp';
      case CommunicationType.EMAIL:
        return 'gmail';
      case CommunicationType.PUSH_NOTIFICATION:
        return 'notification';
      default:
        return 'whatsapp';
    }
  };

  const handleOnToggleChanel = (channel: string): void => {
    setSelectedChannels(prev => {
      const next = prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel];
      return next;
    });
  };

  const getCitiesByUF = async (uf: string): Promise<OptionProps[]> => {
    const key = uf.trim().toUpperCase();

    if (!key) return [];

    const res = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${key}/municipios`,
    );

    if (!res.ok) {
      throw new Error(`IBGE erro ao buscar cidades de ${key}`);
    }

    const data: IbgeMunicipioDTO[] = await res.json();

    const options: OptionProps[] = data
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(c => ({
        value: c.nome,
        label: c.nome,
      }));

    setCities(options);
    return options;
  };

  const handleOntypeChange = (value: 'TEMPLATE' | 'CUSTOM'): void => {
    setType(value);
    onChangeFormInputCommunication(FormCommunication.type_communication)(value);

    if (value === 'TEMPLATE') {
      onChangeFormInputCommunication(FormCommunication.messages)('');
    } else {
      onChangeFormInputCommunication(FormCommunication.template)('');
    }
  };

  const handleAddCity = (): void => {
    if (!currentCityOption?.value) return;

    setSelectedCities(prev => {
      const exists = prev.some(c => c.value === currentCityOption.value);
      if (exists) return prev;
      return [...prev, currentCityOption];
    });

    onChangeFormInputCommunication(FormCommunication.city)('');
    setCurrentCityOption(null);
  };

  const handleRemoveCity = (value?: string | number): void => {
    setSelectedCities(prev => prev.filter(c => c.value !== value));
  };

  const handleLoadCommunication = async (): Promise<void> => {
    if (!id) {
      toast.error('Campanha não informada');
      history.push('/communication');
      return;
    }

    try {
      setStatus(States.loading);

      const response: Communication = await communicationService.findById(id);

      const campaignState = response.state || response.locations?.[0]?.state || '';

      const campaignCities = response.cities?.length
        ? response.cities
        : response.locations?.map(location => location.city) || [];

      const campaignRadius = response.radius ?? response.locations?.[0]?.radius ?? '';

      const campaignChannels = response.channels?.map(handleCommunicationTypeToString) || [];

      const selectedCityOptions: OptionProps[] = campaignCities.map(city => ({
        value: city,
        label: city,
      }));

      onChangeFormInputCommunication(FormCommunication.name)(response.name || '');
      onChangeFormInputCommunication(FormCommunication.country)(campaignState);
      onChangeFormInputCommunication(FormCommunication.radius)(String(campaignRadius || ''));
      onChangeFormInputCommunication(FormCommunication.events)(response.eventId || '');
      onChangeFormInputCommunication(FormCommunication.messageAmount)(
        String(response.customerLimit ?? ''),
      );
      onChangeFormInputCommunication(FormCommunication.startDate)(
        toDateTimeLocal(response.startDate),
      );
      onChangeFormInputCommunication(FormCommunication.endDate)(toDateTimeLocal(response.endDate));
      onChangeFormInputCommunication(FormCommunication.imageBase64)(response.imageBase64 || '');
      setImageFileName(response.imageBase64?.split('/').pop() || '');

      if (response.template) {
        setType('TEMPLATE');
        onChangeFormInputCommunication(FormCommunication.type_communication)('TEMPLATE');
        onChangeFormInputCommunication(FormCommunication.template)(response.template);
        onChangeFormInputCommunication(FormCommunication.messages)('');
      } else {
        setType('CUSTOM');
        onChangeFormInputCommunication(FormCommunication.type_communication)('CUSTOM');
        onChangeFormInputCommunication(FormCommunication.messages)(response.mensage || '');
        onChangeFormInputCommunication(FormCommunication.template)('');
      }

      setSelectedChannels(campaignChannels);
      setSelectedCities(selectedCityOptions);

      if (campaignState) {
        await getCitiesByUF(campaignState);
      }
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(err.response?.data?.message || err.message || 'Erro ao carregar campanha');
      history.push('/communication');
    } finally {
      setStatus(States.default);
    }
  };

  const handleOnsubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (!id) {
      toast.error('Campanha não informada');
      return;
    }

    try {
      setStatus(States.loading);

      const types: CommunicationType[] = selectedChannels.map(handleCommunicationType);

      if (types.length === 0) {
        toast.error('Informe ao menos um canal!');
        return;
      }

      if (selectedCities.length === 0) {
        toast.error('Informe ao menos uma cidade!');
        return;
      }

      if (!formDataCommunication[FormCommunication.events]) {
        toast.error('Informe um evento!');
        return;
      }
      const periodError = getCommunicationPeriodError(
        formDataCommunication[FormCommunication.startDate],
        formDataCommunication[FormCommunication.endDate],
      );
      if (periodError) {
        toast.error(periodError);
        return;
      }

      const newCities: string[] = selectedCities.map(city => city.label as string);

      const payload = {
        name: formDataCommunication[FormCommunication.name],
        communicationChanel: types,
        state: formDataCommunication[FormCommunication.country],
        cities: newCities,
        customerLimit: Number(formDataCommunication[FormCommunication.messageAmount]),
        event: formDataCommunication[FormCommunication.events],
        template: formDataCommunication[FormCommunication.template]
          ? formDataCommunication[FormCommunication.template]
          : undefined,
        imageBase64: formDataCommunication[FormCommunication.imageBase64] || undefined,
        mensage: formDataCommunication[FormCommunication.messages]
          ? formDataCommunication[FormCommunication.messages]
          : undefined,
        startDate: toCommunicationDateISOString(formDataCommunication[FormCommunication.startDate]),
        endDate: toCommunicationDateISOString(formDataCommunication[FormCommunication.endDate]),
        radius: Number(formDataCommunication[FormCommunication.radius]),
      } as CreateCommunication;

      await communicationService.update(id, payload as any);

      toast.success('Campanha alterada com sucesso!');
      history.push('/communication');
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(err.response?.data?.message || err.message || 'Erro ao alterar campanha');
    } finally {
      setStatus(States.default);
    }
  };

  const handleImageChange = (file?: File): void => {
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png)$/)) {
      toast.error('A imagem da campanha deve ser JPG, JPEG ou PNG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem da campanha deve ter no máximo 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result?.toString();
      if (base64) {
        setImageFileName(file.name);
        onChangeFormInputCommunication(FormCommunication.imageBase64)(base64);
      }
    };
    reader.onerror = () => toast.error('Não foi possível ler a imagem da campanha.');
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    handleOnGetEvents();
  }, []);

  useEffect(() => {
    handleLoadCommunication();
  }, [id]);

  return (
    <CommunicationEditUI
      state={state}
      formData={formDataCommunication}
      formErrors={formErrorsCommunication}
      selectedChannels={selectedChannels}
      onChangeFormInput={onChangeFormInputCommunication}
      onSubmit={handleOnsubmit}
      events={events}
      type={type}
      toggleChannel={handleOnToggleChanel}
      onTypeChange={handleOntypeChange}
      cities={cities}
      getCities={getCitiesByUF}
      currentCityOption={currentCityOption}
      setCurrentCityOption={setCurrentCityOption}
      selectedCities={selectedCities}
      onAddCity={handleAddCity}
      onRemoveCity={handleRemoveCity}
      imageFileName={imageFileName}
      onChangeImage={handleImageChange}
    />
  );
};
